import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import type { SelectChangeEvent } from '@mui/material';
import { useAuth } from '@core/auth/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import type { AppLanguage } from '@app/i18n';
import { DEFAULT_LANGUAGE, normalizeAppLanguage, setAppLanguage } from '@app/i18n';

interface HeaderProps {
  onMenuClick: () => void;
  /** Mobile hamburger; hidden on pages without a sidebar (module chooser) */
  showMenuButton?: boolean;
}

// White-on-brand tones for elements placed on the blue header
const onBrandMuted = 'rgba(255, 255, 255, 0.8)';
const onBrandHover = 'rgba(255, 255, 255, 0.12)';

/**
 * Application Header
 *
 * Responsive header with:
 * - Mobile: Hamburger menu button
 * - App name (Latin / Cyrillic)
 * - Language selector
 * - User info and logout
 */
export const Header = ({ onMenuClick, showMenuButton = true }: HeaderProps) => {
  const { user, logout } = useAuth();
  const { t: tx, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const currentLanguage: AppLanguage = normalizeAppLanguage(i18n.language) ?? DEFAULT_LANGUAGE;

  const handleLanguageChange = (event: SelectChangeEvent<AppLanguage>) => {
    void setAppLanguage(event.target.value as AppLanguage);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAccountClick = () => {
    handleMenuClose();
    navigate('/vozni-park/profil');
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    logout();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'primary.main', // Brand blue from the logo
        color: 'primary.contrastText',
      }}
    >
      <Container maxWidth={false} disableGutters>
        <Toolbar
          sx={{
            px: { xs: 1, sm: 2 },
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          {/* Mobile menu button */}
          {isMobile && showMenuButton && (
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={onMenuClick}
              sx={{
                mr: 1,
                color: 'inherit',
                '&:hover': {
                  bgcolor: onBrandHover,
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* App name (follows the selected script) - the logo lives on the login page */}
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            flexGrow={1}
            minWidth={0} // Allow flex item to shrink below content size
          >
            <Typography
              component="div"
              noWrap
              sx={{
                fontSize: { xs: '1rem', sm: '1.125rem' },
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: 0.2,
              }}
            >
              {tx('app.title')}
            </Typography>
            <Typography
              component="div"
              noWrap
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontSize: '0.75rem',
                lineHeight: 1.3,
                color: onBrandMuted,
              }}
            >
              {tx('app.company')}
            </Typography>
          </Box>

          {/* Right side actions */}
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 0.5, sm: 2 }}
            flexShrink={0}
          >
            {/* Language selector - minimal style with only arrow */}
            <Select
              size="small"
              value={currentLanguage}
              onChange={handleLanguageChange}
              // Mobile shows a short label (Lat / Ćir); the menu keeps full names
              renderValue={(value) =>
                isMobile
                  ? tx(value === 'sr-Cyrl' ? 'language.cyrillicShort' : 'language.latinShort')
                  : tx(value === 'sr-Cyrl' ? 'language.cyrillic' : 'language.latin')
              }
              variant="standard"
              disableUnderline
              sx={{
                color: onBrandMuted,
                minWidth: { xs: 60, sm: 90 },
                '& .MuiSelect-select': {
                  py: 0.5,
                  pr: '20px !important',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&:focus': {
                    bgcolor: 'transparent',
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: onBrandMuted,
                  right: 0,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                },
                '&:hover': {
                  color: 'primary.contrastText',
                  '& .MuiSvgIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <MenuItem value="sr-Latn">{tx('language.latin')}</MenuItem>
              <MenuItem value="sr-Cyrl">{tx('language.cyrillic')}</MenuItem>
            </Select>

            {/* Username (hide on mobile) */}
            {user?.username && !isMobile && (
              <Typography
                variant="body2"
                noWrap
                sx={{ color: onBrandMuted }}
              >
                {user.username}
              </Typography>
            )}

            {/* User menu button */}
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{
                color: 'inherit',
                '&:hover': {
                  bgcolor: onBrandHover,
                },
              }}
              aria-label="user menu"
              aria-controls={isMenuOpen ? 'user-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isMenuOpen ? 'true' : undefined}
            >
              <AccountCircleIcon />
            </IconButton>

            {/* User dropdown menu */}
            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'user-menu-button',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleAccountClick}>
                {tx('common.myAccount')}
              </MenuItem>
              <MenuItem onClick={handleLogoutClick}>
                {tx('common.logout')}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

